import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTimer } from "react-timer-hook";
import { useLanguage } from "../../context/Language/LanguageContext";
import { drawArrowHead, generateUUID } from "../utils/Utils";

/**
 * DrawAndCodeCourt
 * --------------------------------------------------
 * "Draw & Code" — an alternative to typing/tapping DV codes: the user draws
 * the ball path directly on a court diagram (drag with mouse/finger) and the
 * component infers the sequence of events (serve/reception/set, attack/
 * block/dig/set) and reports them back to the caller as fully-formed event
 * objects (same shape produced by DVMatchCoding's `parseDVCode`).
 *
 * This is intentionally a first pass ("not refined") — grades/evaluations are
 * mostly fixed defaults per the documented rules below rather than computed
 * from precise touch quality, and DVMatchCoding's own engine (applyEvents /
 * rallyWinnerFor) still owns scoring, rotation, and serve/side-out — this
 * component only has to decide WHO touched the ball, WHAT skill, WHAT (rough)
 * result, and WHERE (DV zone), then hand a small ordered batch of events back.
 *
 * Interaction model (one drag gesture = one "action group"):
 *   - mouse/finger down on the acting player's side          -> ballstart
 *   - drag; if the user pauses (~0.4s) a touch point freezes  -> ballmid
 *   - release                                                  -> ballend
 *   Straight drag (no pause) + release = a terminal action (ace/error or
 *   kill/error). A pause in the middle means the ball was touched again
 *   (reception, dig, or a block touch) — see the rules below.
 *
 *   A block touch (a pause near the net during an attack) supports a SECOND
 *   pause:
 *     - Release right after the net pause -> the rally ends. If the ball
 *       falls untouched in the attacker's OWN court (in bounds) it's a
 *       blocked kill: Attack "/" + a DEFERRED Block "#" (the next tap/click
 *       selects the blocking player -> point to the blocking team). Anywhere
 *       else (out of bounds on the attacker's side, or over on the blocking
 *       team's side) the attacking team scores: Attack "#" + an optional
 *       DEFERRED Block "=" (compoundPaired, so it never double-scores).
 *     - Keep holding and pause again elsewhere -> the block was COVERED and
 *       play continues (the 3-legged attacker -> blocker -> defender ->
 *       setter path). Three touches are recorded: Attack (into the block),
 *       Block "+" (the deflection that kept the ball alive, auto-attributed
 *       to the nearest front-row blocker — NOT deferred, since the gesture is
 *       already complete), then a Dig (graded/attributed by
 *       `passGradeForPoint`/whichever half the second pause lands in — the
 *       attacking team's half means it covered its own blocked ball, the
 *       blocking team's half means the block deflected onto their side and
 *       they covered it), with the ball's final destination determining an
 *       over-pass exactly like any other dig.
 *
 * Rotation / role model:
 *   Each player is classified S / OPP / OH / MB by their OFFSET from the
 *   setter (offset 0 = S, 3 = OPP, {1,4} = the two OHs, {2,5} = the two MBs),
 *   derived FRESH each render from the CURRENT lineup + current setter (NOT a
 *   frozen "starting lineup" snapshot). This classification is ROTATION-
 *   INVARIANT (all six players rotate in lockstep, so the gap to the setter
 *   never changes) AND stable across a double substitution (each pair maps
 *   back onto itself). The "1" vs "2" numbering within the OH and MB pairs is
 *   PINNED to the players by shirt number (lower shirt = "1") rather than the
 *   offset, so a given outside hitter / middle blocker keeps the same OH1/OH2
 *   or MB1/MB2 label through a double sub (which jumps the setter to the
 *   diagonal slot and would otherwise flip the offset-based numbering) as well
 *   as every rotation and undo. Liberos get L1/L2 by their order in the team's
 *   designated-libero list. The libero (L1) swaps onto the court for whichever
 *   middle blocker (MB1/MB2) is currently in a back-row position (1, 5, 6)
 *   while that team is NOT serving; role display then shows the libero's own
 *   L1/L2 label.
 *
 *   Player POSITIONING on the diagram follows the same rotation-array model
 *   as the original CodingCourt.jsx: `origpos` while a team is serving,
 *   `recpos`/`recposopp` while receiving serve, `basepos` once the rally is
 *   in general play ("attack" stage). Each table has 6 rows ordered by
 *   DVMatchCoding's own rotation number R1..R6 (row 0 = R1/setter-in-1,
 *   row 1 = R2/setter-in-6, row 2 = R3/setter-in-5, row 3 = R4/setter-in-4,
 *   row 4 = R5/setter-in-3, row 5 = R6/setter-in-2). The array ROW INDEX is
 *   taken directly from DVMatchCoding's own authoritative `rotationA`/
 *   `rotationB` counter (`team.rotation`, already correct there) via
 *   `rowIndex = (rotation - 1) mod 6` — no separate lineup/setter lookup.
 */

// Standard court-position formation percentages (0-100), one row per rotation
// (row comments below give the R↔S mapping baked into these tables), each
// row holding {x,y} for on-court positions 1..6 in order. Copied from
// CodingCourt.jsx (basepos = general rally/base defense formation).
const basepos = [
  [
    { x: 16, y: 40 },
    { x: 100, y: 80 },
    { x: 50, y: 80 },
    { x: 0, y: 80 },
    { x: 50, y: 33 },
    { x: 84, y: 40 },
  ], // R1 - S1
  [
    { x: 16, y: 40 },
    { x: 50, y: 33 },
    { x: 50, y: 80 },
    { x: 0, y: 80 },
    { x: 100, y: 80 },
    { x: 84, y: 40 },
  ], // R2 - S6
  [
    { x: 16, y: 40 },
    { x: 50, y: 33 },
    { x: 84, y: 40 },
    { x: 0, y: 80 },
    { x: 100, y: 80 },
    { x: 50, y: 80 },
  ], // R3 - S5
  [
    { x: 0, y: 80 },
    { x: 50, y: 33 },
    { x: 84, y: 40 },
    { x: 16, y: 40 },
    { x: 100, y: 80 },
    { x: 50, y: 80 },
  ], // R4 - S4
  [
    { x: 0, y: 80 },
    { x: 100, y: 80 },
    { x: 84, y: 40 },
    { x: 16, y: 40 },
    { x: 50, y: 33 },
    { x: 50, y: 80 },
  ], // R5 - S3
  [
    { x: 0, y: 80 },
    { x: 100, y: 80 },
    { x: 50, y: 80 },
    { x: 16, y: 40 },
    { x: 50, y: 33 },
    { x: 84, y: 40 },
  ], // R6 - S2
];

// Serving-formation coordinates (origpos, per rotation), also from CodingCourt.jsx.
const origpos = [
  [
    { x: 16, y: 2 },
    { x: 16, y: 66 },
    { x: 50, y: 66 },
    { x: 84, y: 66 },
    { x: 84, y: 22 },
    { x: 50, y: 22 },
  ], // R1 - S1
  [
    { x: 50, y: 22 },
    { x: 16, y: 2 },
    { x: 16, y: 66 },
    { x: 50, y: 66 },
    { x: 84, y: 66 },
    { x: 84, y: 22 },
  ], // R2 - S6
  [
    { x: 84, y: 22 },
    { x: 50, y: 22 },
    { x: 16, y: 2 },
    { x: 16, y: 66 },
    { x: 50, y: 66 },
    { x: 84, y: 66 },
  ], // R3 - S5
  [
    { x: 84, y: 66 },
    { x: 84, y: 22 },
    { x: 50, y: 22 },
    { x: 16, y: 2 },
    { x: 16, y: 66 },
    { x: 50, y: 66 },
  ], // R4 - S4
  [
    { x: 50, y: 66 },
    { x: 84, y: 66 },
    { x: 84, y: 22 },
    { x: 50, y: 22 },
    { x: 16, y: 2 },
    { x: 16, y: 66 },
  ], // R5 - S3
  [
    { x: 16, y: 66 },
    { x: 50, y: 66 },
    { x: 84, y: 66 },
    { x: 84, y: 22 },
    { x: 50, y: 22 },
    { x: 16, y: 2 },
  ], // R6 - S2
];

const recpos = [
  [
    { x: 8, y: 30 },
    { x: 16, y: 45 },
    { x: 55, y: 80 },
    { x: 90, y: 75 },
    { x: 82, y: 38 },
    { x: 50, y: 32 },
  ], // R1 - S1
  [
    { x: 40, y: 62 },
    { x: 16, y: 33 },
    { x: 8, y: 70 },
    { x: 33, y: 85 },
    { x: 82, y: 45 },
    { x: 50, y: 25 },
  ], // R2 - S6
  [
    { x: 70, y: 68 },
    { x: 52, y: 30 },
    { x: 25, y: 36 },
    { x: 20, y: 75 },
    { x: 82, y: 38 },
    { x: 90, y: 85 },
  ], // R3 - S5
  [
    { x: 92, y: 85 },
    { x: 50, y: 30 },
    { x: 20, y: 33 },
    { x: 10, y: 10 },
    { x: 80, y: 35 },
    { x: 85, y: 65 },
  ], // R4 - S4
  [
    { x: 44, y: 85 },
    { x: 85, y: 40 },
    { x: 52, y: 30 },
    { x: 33, y: 10 },
    { x: 22, y: 32 },
    { x: 16, y: 66 },
  ], // R5 - S3
  [
    { x: 30, y: 85 },
    { x: 85, y: 40 },
    { x: 90, y: 85 },
    { x: 70, y: 16 },
    { x: 50, y: 28 },
    { x: 16, y: 45 },
  ], // R6 - S2
];

const recposopp = [
  [
    { x: 12, y: 52 },
    { x: 16, y: 68 },
    { x: 55, y: 82 },
    { x: 82, y: 38 },
    { x: 54, y: 28 },
    { x: 24, y: 32 },
  ], // R1 - S1
  [
    { x: 40, y: 62 },
    { x: 16, y: 33 },
    { x: 8, y: 70 },
    { x: 33, y: 85 },
    { x: 82, y: 45 },
    { x: 50, y: 25 },
  ], // R2 - S6
  [
    { x: 80, y: 68 },
    { x: 70, y: 30 },
    { x: 45, y: 28 },
    { x: 20, y: 42 },
    { x: 82, y: 45 },
    { x: 90, y: 85 },
  ], // R3 - S5
  [
    { x: 90, y: 85 },
    { x: 80, y: 28 },
    { x: 52, y: 20 },
    { x: 26, y: 28 },
    { x: 75, y: 50 },
    { x: 85, y: 65 },
  ], // R4 - S4
  [
    { x: 50, y: 85 },
    { x: 85, y: 66 },
    { x: 80, y: 33 },
    { x: 50, y: 16 },
    { x: 16, y: 30 },
    { x: 10, y: 66 },
  ], // R5 - S3
  [
    { x: 30, y: 85 },
    { x: 85, y: 60 },
    { x: 90, y: 85 },
    { x: 75, y: 32 },
    { x: 48, y: 29 },
    { x: 16, y: 35 },
  ], // R6 - S2
];

const BACK_ROW_POSITIONS = new Set([1, 5, 6]);

const SKILL_LABELS = {
  S: "Serve",
  R: "Reception",
  E: "Setter Call",
  A: "Attack",
  B: "Block",
  D: "Dig",
};

function shirtOf(player) {
  if (!player) return null;
  const n =
    player.shirtNumber ?? player.ShirtNumber ?? player.playerNumber ?? null;
  if (n == null || n === "") return null;
  const parsed = parseInt(String(n), 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function positionOf(player) {
  const p = parseInt(
    String(player?.position ?? player?.currentPosition ?? ""),
    10,
  );
  return Number.isFinite(p) ? p : null;
}

// Array row index for a given DVMatchCoding rotation number (its own
// rotationA/rotationB counter, R1..R6 — already correct/authoritative there,
// incremented on every side-out). Row 0 = R1 (setter in position 1), row 1 =
// R2 (setter in 6), row 2 = R3 (setter in 5), row 3 = R4 (setter in 4),
// row 4 = R5 (setter in 3), row 5 = R6 (setter in 2) — i.e. rowIndex = R - 1.
function rotationRowForRotationNumber(rotationNumber) {
  return (((rotationNumber - 1) % 6) + 6) % 6;
}

// Picks the formation row for a team, using DVMatchCoding's OWN rotation
// counter (`team.rotation`, i.e. rotationA/rotationB) directly — that value
// is already correct/authoritative there, so we just map it straight onto
// the matching array row instead of re-deriving it from lineup lookups.
function formationFor(team, servingTeamKey, currentStage) {
  const rotationIdx = rotationRowForRotationNumber(team?.rotation ?? 1);
  console.log(
    "formationFor",
    team?.key,
    "rotation",
    team?.rotation,
    "rotationIdx",
    rotationIdx,
    "servingTeamKey",
    servingTeamKey,
    "currentStage",
    currentStage,
  );
  let arr;
  if (currentStage === "attack") arr = basepos;
  else if (servingTeamKey === team.key) arr = origpos;
  else arr = team?.isOppPassing ? recposopp : recpos;
  console.log("formationFor", team?.key, "arr", arr);
  return arr[rotationIdx];
  // if (currentStage === "attack") {
  //   return basepos[0];
  // }
  // else if (servingTeamKey === team.key) {
  //   return origpos[0];
  // }
  // else {
  //   arr = team?.isOppPassing ? recposopp : recpos;
  //   return arr[0];
  // }
}

// Builds a shirt-number -> role map from a team's CURRENT lineup + setter.
// The S / OPP / OH / MB CLASSIFICATION is by OFFSET from the setter (offset 0
// = S, 3 = OPP, {1,4} = the two OHs, {2,5} = the two MBs). That classification
// is stable across a double substitution because each pair maps back onto
// itself (a double sub jumps the setter to the diagonal slot, i.e. shifts
// every offset by 3, which swaps 1<->4 and 2<->5 WITHIN each pair). The "1"
// vs "2" numbering within the OH and MB pairs is therefore NOT taken from the
// offset (which would flip on a double sub) but PINNED to the players by shirt
// number (lower shirt = "1") — so a given outside hitter / middle blocker
// keeps the same OH1/OH2 / MB1/MB2 label through a double sub (and every
// rotation and undo), while S/OPP still correctly follow whoever the current
// setter and their diagonal are.
function buildRoleByShirt(lineup, setter, libero) {
  const map = {};
  const arr = Array.isArray(lineup) ? lineup : [];
  const setterShirt = setter != null ? parseInt(String(setter), 10) : null;
  const setterEntry = arr.find((p) => shirtOf(p) === setterShirt);
  const setterPos = positionOf(setterEntry) ?? 1;
  const ohShirts = [];
  const mbShirts = [];
  arr.forEach((p) => {
    const shirt = shirtOf(p);
    const pos = positionOf(p);
    if (shirt == null || pos == null) return;
    const offset = (((pos - setterPos) % 6) + 6) % 6;
    if (offset === 0) map[shirt] = "S";
    else if (offset === 3) map[shirt] = "OPP";
    else if (offset === 1 || offset === 4) ohShirts.push(shirt);
    else mbShirts.push(shirt); // offset 2 or 5 -> middle blocker
  });
  // Pin OH1/OH2 and MB1/MB2 to the players (lower shirt number = "1") so the
  // labels never swap when a double sub flips the offsets within each pair.
  ohShirts
    .sort((a, b) => a - b)
    .forEach((shirt, i) => {
      map[shirt] = i === 0 ? "OH1" : "OH2";
    });
  mbShirts
    .sort((a, b) => a - b)
    .forEach((shirt, i) => {
      map[shirt] = i === 0 ? "MB1" : "MB2";
    });
  (Array.isArray(libero) ? libero : []).forEach((shirt, idx) => {
    const n = parseInt(String(shirt), 10);
    if (Number.isFinite(n)) map[n] = idx === 0 ? "L1" : "L2";
  });
  return map;
}

// Builds the 6 on-court slots {position, shirtNumber, role, isLibero} for a
// team, substituting the primary libero at whichever back-row slot currently
// holds a middle blocker while that team is NOT serving. `roleByShirt` (from
// `buildRoleByShirt`) supplies each shirt's FIXED role.
function buildCourtSlots(team, isServingNow, roleByShirt, activeLiberoIdx = 0) {
  const lineup = team?.lineup ?? [];
  const liberoList = Array.isArray(team?.libero) ? team.libero : [];
  const liberoShirt =
    liberoList.length > 0
      ? (liberoList[activeLiberoIdx] ?? liberoList[0])
      : null;

  const slots = [];
  for (let pos = 1; pos <= 6; pos++) {
    const player = lineup.find((p) => positionOf(p) === pos);
    // The formation tables (basepos/origpos/recpos/recposopp, copied verbatim
    // from CodingCourt.jsx) are indexed by each player's FIXED SEAT — the
    // position they'd occupy if the team's rotation counter were at R1 —
    // NOT by their current, dynamically rotating position. Confirmed
    // directly against CodingCourt.jsx's own `drawPlayers`: it matches
    // `ppos[index]` (where `ppos = origpos[team.rotation - 1]`, i.e. the ROW
    // ALREADY selected for the CURRENT rotation) against `p.position ===
    // index + 1`, and the whole formation only makes physical sense (e.g.
    // the "about to serve" baseline coordinate always landing on whoever is
    // CURRENTLY the server) when the index is this fixed per-player seat.
    // Using the CURRENT position directly here would double-apply the
    // rotation (once via the row, once via the index), producing a
    // formation shifted by however many side-outs have happened since
    // kickoff — exactly "discs shown one position further than actual"
    // after one rotation, further still after more. The visual rotation
    // effect comes ENTIRELY from `formationFor`'s row selection.
    // A player's seat is derivable fresh, every render, straight from their
    // CURRENT position + the team's CURRENT rotation number — no need for a
    // frozen "starting lineup" snapshot (which would also need special-
    // casing for substitutes): rotating forward decreases position by 1
    // (mod 6, wrapping 1<->6, see ROTATE_NEXT in DVMatchCoding.jsx), so
    // reversing that by (rotation - 1) steps recovers "what position was
    // this player at when the team was last at R1" — a value that stays
    // constant for a given player across every subsequent rotation.
    const stepsSinceR1 = (((team?.rotation ?? 1) - 1) % 6 + 6) % 6;
    const seat = (((pos - 1 + stepsSinceR1) % 6) + 6) % 6 + 1;
    const rawShirt = shirtOf(player);
    const role = rawShirt != null ? (roleByShirt?.[rawShirt] ?? "") : "";
    let shirtNumber = rawShirt;
    let isLibero = false;
    // The libero replaces whichever middle blocker (MB1/MB2) is CURRENTLY in
    // a back-row position (1, 5, 6) — since MB1/MB2 alternate between front
    // and back row every rotation, this position-based check (re-evaluated
    // fresh every render from the team's live `lineup`) is what makes the
    // libero automatically come OFF and the real MB come back ON as soon as
    // that MB rotates into the front row (2, 3, 4): BACK_ROW_POSITIONS no
    // longer matches their position, so this whole branch is skipped and
    // `shirtNumber`/`isLibero` are left at the real MB's own values.
    if ((role === "MB1" || role === "MB2") && liberoShirt != null) {
      if (BACK_ROW_POSITIONS.has(pos)) {
        // The one exception: a serving MB (position 1, while this team is
        // serving) stays on court instead of being substituted.
        if (!(pos === 1 && isServingNow)) {
          shirtNumber = liberoShirt;
          isLibero = true;
        }
      }
    }
    const displayRole = isLibero ? (roleByShirt?.[shirtNumber] ?? "L") : role;
    slots.push({
      position: pos,
      seat,
      shirtNumber,
      rawShirtNumber: rawShirt,
      role: displayRole,
      isLibero,
    });
  }
  return slots;
}

function makeEvent(
  teamKey,
  shirtNumber,
  skill,
  evaluation,
  zoneFrom,
  zoneTo,
  subzoneFrom,
  subzoneTo,
  // Precise dragged-point percent coords (0-100, canonical DVMatchCoding
  // frame — see `canonicalPctForPoint`) for the from/to touch, persisted
  // alongside the DV zone/subzone digits so a path drawn here renders with
  // the same pixel-accurate precision as a manually-dragged edit made on
  // DVMatchCoding's own court diagram (`ballStartPct`/`ballEndPct`/
  // `ballMidPct`, read back by `eventPath`/`pctToCourtPoint`), instead of
  // always snapping to the centre of whichever zone+subzone quadrant the
  // touch landed in.
  ballStartPct,
  ballEndPct,
) {
  const padded =
    shirtNumber != null
      ? String(parseInt(String(shirtNumber), 10)).padStart(2, "0")
      : "00";
  const teamMark = teamKey === "A" ? "*" : "a";
  const ballMidPct =
    ballStartPct && ballEndPct
      ? { x: (ballStartPct.x + ballEndPct.x) / 2, y: (ballStartPct.y + ballEndPct.y) / 2 }
      : undefined;
  return {
    id: generateUUID(),
    raw: `${teamMark}${padded}${skill}${evaluation}${zoneFrom ?? ""}${subzoneFrom ?? ""}${zoneTo ?? ""}${subzoneTo ?? ""} (draw)`,
    team: teamKey,
    shirtNumber: padded,
    skill,
    skillLabel: SKILL_LABELS[skill] ?? skill,
    type: null,
    evaluation,
    evaluationLabel: null,
    zoneFrom: zoneFrom ?? null,
    subzoneFrom: subzoneFrom ?? null,
    zoneTo: zoneTo ?? null,
    subzoneTo: subzoneTo ?? null,
    ...(ballStartPct && { ballStartPct }),
    ...(ballEndPct && { ballEndPct }),
    ...(ballMidPct && { ballMidPct }),
  };
}

// Standard DV zone layout, viewed from a team's OWN perspective (own-left
// is column 0): front row nearest the net is 4/3/2, back row is 5/6/1 — see
// `zoneAndSubzoneForPoint`, which mirrors DVMatchCoding's `ZONE_GRID` /
// `internalToZone`/`subzonePoint` logic.
const ZONE_GRID_ROWS = [
  ["4", "3", "2"],
  ["7", "8", "9"],
  ["5", "6", "1"],
];

// Ideal serve-receive target ("perfect setter position"), expressed in the
// SAME 0-100 formation-percentage system as basepos/origpos/recpos (see
// `pixelForFormationPoint`), within the RECEIVING team's own half. A pass
// landing within ~1m of this spot is graded "#" (perfect), ~2m "+" (good),
// ~3m "!" (ok), further away "-" (poor); a pass that crosses back over the
// net into the SERVING team's court is always "/" (over-pass) regardless of
// distance. Each court half is ~9m deep/wide (a full court is 18m x 9m),
// used to convert the on-screen distance into metres.
const PERFECT_SETTER_POSITION = { x: 30, y: 85 };
const METRES_PER_HALF = 9;

// Where a team's bench-libero marker is drawn, in the SAME 0-100
// formation-percentage system as basepos/origpos/recpos (see
// `pixelForFormationPoint`) — x > 100 sits just outside the sideline (to
// the "right" in vertical orientation / "top" in horizontal orientation),
// y close to 0 sits just outside the team's own baseline.
const SECOND_LIBERO_POSITION = { x: 115, y: 12 };

// Draws a small rounded-pill role badge (S/OPP/OH1/OH2/MB1/MB2/L1/L2) just
// below (or, when `placeAbove` is set, above) a player's disc so roles are
// visible at a glance while drawing.
// Returns black or white, whichever contrasts better against `colour` — same
// logic as DVMatchCoding's own `contrastFor` (kept as a small local copy here
// rather than importing from that file) so player discs read consistently
// with the rest of the app's team-colour styling.
function contrastFor(colour) {
  if (!colour) return "#ffffff";
  const c = colour.trim();
  const m = c.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (m) {
    let h = m[1];
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55
      ? "#000000"
      : "#ffffff";
  }
  const light = new Set([
    "white",
    "yellow",
    "lime",
    "cyan",
    "aqua",
    "silver",
    "ivory",
    "snow",
    "cornsilk",
    "linen",
    "oldlace",
    "floralwhite",
    "seashell",
    "lightyellow",
    "lightcyan",
    "lightgreen",
    "palegreen",
    "mintcream",
    "honeydew",
    "aliceblue",
    "lavender",
    "lightblue",
    "lightpink",
    "lightsalmon",
    "gold",
    "orange",
    "khaki",
    "wheat",
    "bisque",
    "moccasin",
    "peachpuff",
    "mistyrose",
    "greenyellow",
    "yellowgreen",
  ]);
  return light.has(c.toLowerCase()) ? "#000000" : "#ffffff";
}

function drawRoleLabel(ctx, slot, radius, placeAbove) {
  const label = slot.role;
  if (!label) return;
  const fontSize = Math.max(9, radius * 0.42);
  ctx.font = `300 ${Math.round(fontSize)}px Arial`;
  const textWidth = ctx.measureText(label).width;
  const padX = fontSize * 0.4;
  const boxW = textWidth + padX * 2;
  const boxH = fontSize * 1.4;
  const boxX = slot.pixel.x - boxW / 2;
  const boxY = placeAbove
    ? slot.pixel.y - radius * 1.05 - boxH
    : slot.pixel.y + radius * 1.05;
  const r = boxH / 2;
  ctx.beginPath();
  ctx.moveTo(boxX + r, boxY);
  ctx.arcTo(boxX + boxW, boxY, boxX + boxW, boxY + boxH, r);
  ctx.arcTo(boxX + boxW, boxY + boxH, boxX, boxY + boxH, r);
  ctx.arcTo(boxX, boxY + boxH, boxX, boxY, r);
  ctx.arcTo(boxX, boxY, boxX + boxW, boxY, r);
  ctx.closePath();
  ctx.fillStyle = "rgba(17,24,39,0.78)";
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, slot.pixel.x, boxY + boxH / 2);
}

// Draws a solid triangular arrowhead touching the edge of a player's disc,
// pointing `dir` (a unit vector, e.g. [0,1] for "down") away from the disc—
// used to show which team is currently acting and which way the play is
// headed (toward the opposing court). The base of the triangle sits right on
// the disc's edge and is exactly as wide as the disc's diameter; it's filled
// with the same (fully opaque) colour as the disc itself.
function drawDiscArrow(ctx, cx, cy, radius, dir, colour) {
  const [dx, dy] = dir;
  const baseDist = radius * 1; // base sits halfway between centre and edge
  const tipDist = radius * 2.1; // arrow length, proportionate to disc size
  const baseX = cx + dx * baseDist;
  const baseY = cy + dy * baseDist;
  const tipX = cx + dx * tipDist;
  const tipY = cy + dy * tipDist;
  // Perpendicular unit vector — used to spread the two base corners so the
  // base width equals the disc's diameter (2 * radius).
  const px = -dy;
  const py = dx;
  // The "wings" are the two chevron lines running from each base corner in
  // to the tip. Pulling the corners halfway toward the tip makes them 50%
  // shorter while keeping the same angle/shape, just a smaller chevron.
  const wingScale = 0.5;
  const corner1X = tipX + wingScale * (baseX + px * radius - tipX);
  const corner1Y = tipY + wingScale * (baseY + py * radius - tipY);
  const corner2X = tipX + wingScale * (baseX - px * radius - tipX);
  const corner2Y = tipY + wingScale * (baseY - py * radius - tipY);
  //move to centre of disc
  ctx.strokeStyle = "black";
  ctx.lineWidth = radius * 0.1;
  ctx.moveTo(cx, cy);
  ctx.lineTo(tipX, tipY);
  ctx.stroke();
  ctx.moveTo(corner1X, corner1Y);
  ctx.lineTo(tipX, tipY);
  ctx.lineTo(corner2X, corner2Y);
  ctx.stroke();
  ctx.lineWidth = 0;
  // ctx.beginPath();
  // ctx.moveTo(baseX + px * radius, baseY + py * radius);
  // ctx.lineTo(baseX - px * radius, baseY - py * radius);
  // ctx.lineTo(tipX, tipY);
  // ctx.closePath();
  // ctx.fillStyle = colour;
  // ctx.fill();
}

export default function DrawAndCodeCourt({
  orientation,
  primaryTeamKey,
  secondaryTeamKey,
  teamA,
  teamB,
  servingTeam,
  isStartOfRally,
  events,
  onEvents,
  onGoLive,
}) {
  const { t } = useLanguage();
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const isVertical = orientation === "vertical";

  const [size, setSize] = useState({ w: 0, h: 0 });
  const [startPoint, setStartPoint] = useState(null);
  const [midPoint, setMidPoint] = useState(null);
  // Second pause point — only ever captured when the FIRST pause (midPoint)
  // was near the net (a block touch): lets the user hold, pause again at
  // the spot the deflected ball was dug from, then move on to a final
  // release point. See the "attack blocked at the net" rules in
  // `processGesture`.
  const [midPoint2, setMidPoint2] = useState(null);
  const [endPoint, setEndPoint] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [pulse, setPulse] = useState(false);
  // Set right after a single-pause block touch is released (see
  // `processGesture` case 1): holds the info needed to create the Block
  // event once the user taps the blocking player's disc, instead of
  // auto-picking the nearest defender.
  const [pendingBlock, setPendingBlock] = useState(null);
  // Which of a team's (up to 2) designated liberos is currently treated as
  // being on the court, by index into `team.libero` — 0 by default. The
  // OTHER libero (if the team has a second one) is shown as a small "bench"
  // marker just outside the team's own baseline; tapping it swaps the two
  // (see `benchLibero`/`toggleBenchLibero`). Purely a local UI toggle — it
  // doesn't mutate the `teamA`/`teamB` props.
  const [activeLiberoIdx, setActiveLiberoIdx] = useState({ A: 0, B: 0 });
  // Vertical orientation only: the drag/help hint is hidden to reclaim court
  // height and shown on demand via a small "i" button + this popup.
  const [showInfo, setShowInfo] = useState(false);

  // Who acts next — derived PURELY from the current rally's event log (not
  // internal mutable state), so Undo/Redo (or any external change to the set's
  // events) automatically keeps this in sync with the real state of play.
  // A non-terminal Reception/Setter-call/Dig means the team that touched it
  // last is the one to attack next; anything else (or a fresh rally) falls
  // back to the serving team serving.
  const RALLY_CONTINUES_SKILLS = new Set(["R", "E", "D"]);
  const lastEvent = (() => {
    if (!Array.isArray(events)) return null;
    // Substitutions (skill "C") are administrative and don't belong to the
    // rally flow — skip any trailing ones so a sub made mid-set doesn't get
    // treated as the last "real" event (which would flip the stage from the
    // serving phase to "attack"). Mirrors DVMatchCoding's `isStartOfRally`.
    for (let i = events.length - 1; i >= 0; i--) {
      if (events[i]?.skill !== "C") return events[i];
    }
    return null;
  })();
  let stage = "serve";
  let currentTeamKey = servingTeam;
  if (
    !isStartOfRally &&
    lastEvent &&
    (lastEvent.skill === "R" || lastEvent.skill === "D") &&
    lastEvent.evaluation === "/"
  ) {
    // Over-pass: the ball crossed back over the net into the OTHER team's
    // court with no setter call in between — that other team now attacks it.
    stage = "attack";
    currentTeamKey = lastEvent.team === "A" ? "B" : "A";
  } else if (
    !isStartOfRally &&
    lastEvent &&
    RALLY_CONTINUES_SKILLS.has(lastEvent.skill)
  ) {
    stage = "attack";
    currentTeamKey = lastEvent.team;
  } else if (!isStartOfRally && lastEvent) {
    // Unexpected last skill (e.g. a lone manually-typed serve with no
    // matching reception yet) — best-effort default to the receiving team.
    stage = "attack";
    currentTeamKey = servingTeam === "A" ? "B" : "A";
  }

  const { restart } = useTimer({
    expiryTimestamp: new Date(Date.now() + 100000),
    onExpire: () => {
      const ca = computeCourtArea(
        containerRef.current?.clientWidth ?? 0,
        containerRef.current?.clientHeight ?? 0,
      );
      if (!midPointRef.current) {
        const pt = endPointRef.current;
        // A pause only freezes into a mid-point if it lands in the
        // OPPOSITION team's court AND in bounds (a genuine touch —
        // reception/dig), or it's near the net (a block touch, which happens
        // on the attacking side of the net). A serve or spike drawn OUTSIDE
        // the court (out of bounds) is a terminal error with no second
        // touch, so the direction-change timer for a second leg is disabled
        // there — the whole gesture stays a single straight leg.
        const attackingHalf =
          currentTeamKeyRef.current === primaryTeamKeyRef.current
            ? "primary"
            : "secondary";
        const oppositionHalf =
          attackingHalf === "primary" ? "secondary" : "primary";
        const validTouch =
          isNearNet(pt, ca) ||
          (pointHalf(pt, ca) === oppositionHalf && isInBounds(pt, ca));
        if (!validTouch) {
          return;
        }
        setPulse(true);
        setMidPoint(pt);
        return;
      }
      // A second pause is only meaningful after a block touch (a pause near
      // the net) — anywhere else, a single pause is all this gesture model
      // supports (the "plain dig" / straight-attack cases).
      if (!midPoint2Ref.current) {
        if (isNearNet(midPointRef.current, ca)) {
          setPulse(true);
          setMidPoint2(endPointRef.current);
        }
      }
    },
    autoStart: false,
  });

  // Keep ref mirrors of midPoint/midPoint2/endPoint/currentTeamKey/
  // primaryTeamKey so the timer's onExpire (a stale closure) always reads
  // the latest drag position/pause state and attacking-team info.
  const midPointRef = useRef(null);
  useEffect(() => {
    midPointRef.current = midPoint;
  }, [midPoint]);
  const midPoint2Ref = useRef(null);
  useEffect(() => {
    midPoint2Ref.current = midPoint2;
  }, [midPoint2]);
  const endPointRef = useRef(null);
  useEffect(() => {
    endPointRef.current = endPoint;
  }, [endPoint]);
  const currentTeamKeyRef = useRef(currentTeamKey);
  useEffect(() => {
    currentTeamKeyRef.current = currentTeamKey;
  }, [currentTeamKey]);
  const primaryTeamKeyRef = useRef(primaryTeamKey);
  useEffect(() => {
    primaryTeamKeyRef.current = primaryTeamKey;
  }, [primaryTeamKey]);

  // Resize observer — size the canvas to fill its container.
  useEffect(() => {
    if (!containerRef.current) return;
    const node = containerRef.current;
    const update = () => setSize({ w: node.clientWidth, h: node.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  const teamObj = useCallback(
    (key) => (key === "A" ? { ...teamA, key: "A" } : { ...teamB, key: "B" }),
    [teamA, teamB],
  );

  // Stable shirt-number -> role map per team. A player's role is their OFFSET
  // from the setter, which is ROTATION-INVARIANT (every player rotates in
  // lockstep, so the gap between any player and the setter never changes) —
  // so we derive it from the CURRENT lineup, NOT a frozen "starting lineup"
  // snapshot. During normal play this yields the exact same map every
  // rotation; crucially it also stays correct after an UNDO/REDO or a lineup
  // popup edit (both of which restore/replace `team.lineup` but do NOT
  // restore a separate starting-lineup snapshot), so the libero can no longer
  // end up substituted into the wrong back-row slot from a stale role map.
  const roleByShirtA = useMemo(
    () => buildRoleByShirt(teamA?.lineup, teamA?.setter, teamA?.libero),
    [teamA?.lineup, teamA?.setter, teamA?.libero],
  );
  const roleByShirtB = useMemo(
    () => buildRoleByShirt(teamB?.lineup, teamB?.setter, teamB?.libero),
    [teamB?.lineup, teamB?.setter, teamB?.libero],
  );
  const roleByShirtFor = (key) => (key === "A" ? roleByShirtA : roleByShirtB);

  // ---- geometry -------------------------------------------------------
  // Reserves a "serve margin" beyond each baseline (behind position 1/5/6)
  // so the user can start a serve drag from behind the line, like a real
  // server standing off the court. `ca.width`/`ca.height` remain the TRUE
  // court rect (used for in/out and zone checks) — the margin is extra
  // space around it, not part of the court itself.
  const computeCourtArea = (w, h) => {
    const marginFrac = 0.125; // serve margin, as a fraction of the half-court scale
    // Reserve at least this much empty space (px) above and below the whole
    // court view so it never sits flush against the top/bottom edges.
    const OUTER_MARGIN_Y = 15;
    const availH = Math.max(0, h - OUTER_MARGIN_Y * 2);
    // Vertical only: the bench/serving-MB libero markers sit just OUTSIDE each
    // sideline (SECOND_LIBERO_POSITION.x = 115, i.e. 15% of the court width
    // beyond the line) plus their own disc radius (~0.0825 of the scale) —
    // reserve horizontal room on BOTH sides so those markers are fully visible
    // and never clipped by the canvas edge.
    const sideMarginFrac = 0.25;
    let scale;
    if (isVertical) {
      scale = Math.min(w / (1 + 2 * sideMarginFrac), availH / (2 * (1 + marginFrac)));
    } else {
      scale = Math.min(availH, w / (2 * (1 + marginFrac)));
    }
    const width = isVertical ? scale : scale * 2;
    const height = isVertical ? scale * 2 : scale;
    const margin = scale * marginFrac;
    if (isVertical) {
      const totalHeight = height + margin * 2;
      return {
        left: (w - width) / 2,
        top: (h - totalHeight) / 2 + margin,
        width,
        height,
        margin,
      };
    }
    const totalWidth = width + margin * 2;
    return {
      left: (w - totalWidth) / 2 + margin,
      top: (h - height) / 2,
      width,
      height,
      margin,
    };
  };

  const halfScale = (ca) => (isVertical ? ca.width : ca.height);

  const pointHalf = (pt, ca) => {
    if (!pt || !ca) return null;
    if (isVertical) {
      const netY = ca.top + ca.height / 2;
      return pt.y < netY ? "primary" : "secondary";
    }
    const netX = ca.left + ca.width / 2;
    return pt.x < netX ? "primary" : "secondary";
  };

  const isInBounds = (pt, ca) => {
    if (!pt || !ca) return false;
    return (
      pt.x >= ca.left &&
      pt.x <= ca.left + ca.width &&
      pt.y >= ca.top &&
      pt.y <= ca.top + ca.height
    );
  };

  // The serve margin strip (`ca.margin`) behind a team's own baseline — see
  // `computeCourtArea`'s "serve margin" comment. A serve drag must START
  // here (behind the court, on the SERVING team's own side), not from
  // anywhere on the court itself.
  const isInServeZone = (pt, ca, teamKey) => {
    if (!pt || !ca) return false;
    const isTeamPrimary = teamKey === primaryTeamKey;
    if (isVertical) {
      return isTeamPrimary
        ? pt.y >= ca.top - ca.margin &&
            pt.y < ca.top &&
            pt.x >= ca.left &&
            pt.x <= ca.left + ca.width
        : pt.y > ca.top + ca.height &&
            pt.y <= ca.top + ca.height + ca.margin &&
            pt.x >= ca.left &&
            pt.x <= ca.left + ca.width;
    }
    return isTeamPrimary
      ? pt.x >= ca.left - ca.margin &&
          pt.x < ca.left &&
          pt.y >= ca.top &&
          pt.y <= ca.top + ca.height
      : pt.x > ca.left + ca.width &&
          pt.x <= ca.left + ca.width + ca.margin &&
          pt.y >= ca.top &&
          pt.y <= ca.top + ca.height;
  };

  const isNearNet = (pt, ca) => {
    if (!pt || !ca) return false;
    const threshold = halfScale(ca) * 0.05;
    if (isVertical)
      return Math.abs(pt.y - (ca.top + ca.height / 2)) < threshold;
    return Math.abs(pt.x - (ca.left + ca.width / 2)) < threshold;
  };

  // Zone digit + DV-style sub-zone quadrant ("a"=front-left, "b"=front-right,
  // "c"=back-right, "d"=back-left, all from the TOUCHING team's own
  // perspective) for an arbitrary court point — mirrors DVMatchCoding's
  // `internalToZone`/`subzonePoint`. Unlike the old naive row-major grid,
  // this correctly mirrors the left/right axis for whichever half is drawn
  // "facing the opposite way": in vertical orientation that's the PRIMARY
  // (top) team (they face down toward the net, so their own-left is
  // screen-right); in horizontal orientation it's the SECONDARY (right)
  // team (they face left toward the net, so their own-left is screen-bottom).
  // Continuous depth (0 near net .. 1 own baseline) + "own-left..own-right"
  // along-axis (0..1, already mirrored to the touching team's own
  // perspective), plus which team (`A`/`B`) occupies that half, for an
  // arbitrary court point. Shared by `zoneAndSubzoneForPoint` (which bins
  // these into the 3x3 zone/subzone grid) and `canonicalPctForPoint` (which
  // keeps them continuous for precise ball-path storage) so the two always
  // agree on the same underlying geometry.
  const depthAlongForPoint = (pt, ca) => {
    if (!pt || !ca) return null;
    const half = pointHalf(pt, ca);
    if (!half) return null;
    const scale = halfScale(ca);
    let depth, along;
    if (isVertical) {
      const halfTop = half === "primary" ? ca.top : ca.top + scale;
      depth =
        half === "primary"
          ? 1 - (pt.y - halfTop) / scale
          : (pt.y - halfTop) / scale;
      along = (pt.x - ca.left) / ca.width;
    } else {
      const halfLeft = half === "primary" ? ca.left : ca.left + scale;
      depth =
        half === "primary"
          ? 1 - (pt.x - halfLeft) / scale
          : (pt.x - halfLeft) / scale;
      along = (pt.y - ca.top) / ca.height;
    }
    depth = Math.min(1, Math.max(0, depth));
    along = Math.min(1, Math.max(0, along));
    const mirror = isVertical ? half === "primary" : half === "secondary";
    const ownAlong = mirror ? 1 - along : along;
    const team = half === "primary" ? primaryTeamKey : secondaryTeamKey;
    return { half, team, depth, ownAlong };
  };

  // Zone digit + DV-style sub-zone quadrant ("a"=front-left, "b"=front-right,
  // "c"=back-right, "d"=back-left, all from the TOUCHING team's own
  // perspective) for an arbitrary court point — mirrors DVMatchCoding's
  // `internalToZone`/`subzonePoint`. Unlike the old naive row-major grid,
  // this correctly mirrors the left/right axis for whichever half is drawn
  // "facing the opposite way": in vertical orientation that's the PRIMARY
  // (top) team (they face down toward the net, so their own-left is
  // screen-right); in horizontal orientation it's the SECONDARY (right)
  // team (they face left toward the net, so their own-left is screen-bottom).
  const zoneAndSubzoneForPoint = (pt, ca) => {
    const da = depthAlongForPoint(pt, ca);
    if (!da) return { zone: null, subzone: null };
    const { depth, ownAlong } = da;
    const row = Math.min(2, Math.floor(depth * 3));
    const col = Math.min(2, Math.floor(ownAlong * 3));
    const zone = ZONE_GRID_ROWS[row][col];
    // Subzone quadrant within the 3x3 cell, from the team's own perspective.
    const cellDepth = depth * 3 - row; // 0 (near net) .. 1 (near own baseline)
    const cellAlong = ownAlong * 3 - col; // 0 (own-left) .. 1 (own-right)
    const near = cellDepth < 0.5;
    const left = cellAlong < 0.5;
    const subzone = near && left ? "a" : near && !left ? "b" : !near && !left ? "c" : "d";
    return { zone, subzone };
  };

  // Zone digit only (no subzone) — used for zoneFrom points, matching
  // DVMatchCoding's convention of leaving subzoneFrom null (the "from" point
  // is always a known player/touch position, so subzone precision only
  // matters for the destination zone).
  const zoneForPoint = (pt, ca) => zoneAndSubzoneForPoint(pt, ca).zone;

  // Canonical ball-path percent position (0-100 x/y) for an arbitrary court
  // point — the SAME frame DVMatchCoding stores `ballStartPct`/`ballEndPct`/
  // `ballMidPct` in (see its `eventPath`/`pctToCourtPoint`): x=0 is a team's
  // own-left, x=100 own-right; y=50 is the net, y<50 is Team B's half, y>50
  // is Team A's half. This is independent of THIS component's current
  // orientation/primaryTeamKey/swap state, so a path drawn here renders
  // identically on DVMatchCoding's own court diagram regardless of which
  // court view is used later to inspect/edit it.
  const canonicalPctForPoint = (pt, ca) => {
    const da = depthAlongForPoint(pt, ca);
    if (!da) return null;
    const { team, depth, ownAlong } = da;
    return team === "A"
      ? { x: ownAlong * 100, y: 50 + depth * 50 }
      : { x: (1 - ownAlong) * 100, y: 50 - depth * 50 };
  };


  // Pixel position of an arbitrary {x,y} 0-100 formation-percentage point,
  // for a team occupying the given half. Shared by `pixelForSlot` (court
  // slots) and `passGradeForPoint` (the "perfect setter position" target).
  const pixelForFormationPoint = (p, ca, isPrimaryHalf) => {
    // Defensive fallback: an invalid/out-of-range slot index (e.g. from a
    // substitute player not present in the captured starting lineup) would
    // otherwise throw "Cannot read properties of undefined (reading 'x')"
    // here — fall back to the centre of the court instead of crashing.
    if (!p) return { x: ca.left + ca.width / 2, y: ca.top + ca.height / 2 };
    const scale = halfScale(ca);
    if (isVertical) {
      const x = isPrimaryHalf
        ? (p.x * scale) / 100
        : ((100 - p.x) * scale) / 100;
      const y = isPrimaryHalf
        ? (p.y * scale) / 100
        : scale + ((100 - p.y) * scale) / 100;
      return { x: ca.left + x, y: ca.top + y };
    }
    const x = isPrimaryHalf ? (p.x * scale) / 100 : ((100 - p.x) * scale) / 100;
    const y = isPrimaryHalf
      ? (p.y * scale) / 100
      : scale + ((100 - p.y) * scale) / 100;
    return { x: ca.left + y, y: ca.top + ca.height - x };
  };

  // Pixel position of a court slot (1-6) for a team occupying the given half.
  const pixelForSlot = (pos, ppos, ca, isPrimaryHalf) =>
    pixelForFormationPoint(ppos[pos - 1], ca, isPrimaryHalf);

  // Grades a serve-receive touch by its distance from PERFECT_SETTER_POSITION
  // (see comment above the constant) — see the module-level doc for the
  // #/+/!/- distance bands and the over-pass ("/") rule. `receivingIsPrimary`
  // says which half the receiving team currently occupies.
  const passGradeForPoint = (pt, ca, receivingIsPrimary) => {
    if (!pt || !ca) return "-";
    const receivingHalf = receivingIsPrimary ? "primary" : "secondary";
    if (pointHalf(pt, ca) !== receivingHalf) return "/"; // over-pass
    const target = pixelForFormationPoint(
      PERFECT_SETTER_POSITION,
      ca,
      receivingIsPrimary,
    );
    const metresPerPixel = METRES_PER_HALF / halfScale(ca);
    const distMetres =
      Math.hypot(pt.x - target.x, pt.y - target.y) * metresPerPixel;
    if (distMetres <= 1) return "#";
    if (distMetres <= 2) return "+";
    if (distMetres <= 3) return "!";
    return "-";
  };

  // Grades a SERVE and its paired RECEPTION together from the final release
  // point's distance from PERFECT_SETTER_POSITION, per the automatic
  // receive/serve rating spec (deliberately its own lookup table — NOT
  // simply "the reverse of `passGradeForPoint`" — since serve grading has
  // different bands/values than the general dig grading `passGradeForPoint`
  // is also used for elsewhere):
  //   <=1m  -> serve "-", receive "#"
  //   <=2m  -> serve "-", receive "+"
  //   <=3m  -> serve "!", receive "!"
  //   over-pass (crossed back into the SERVING team's court) -> serve "+", receive "/"
  //   otherwise (>3m, still in the receiving team's own court) -> serve "+", receive "-"
  const serveReceiveGradesForPoint = (pt, ca, receivingIsPrimary) => {
    if (!pt || !ca) return { serveGrade: "+", receiveGrade: "-" };
    const receivingHalf = receivingIsPrimary ? "primary" : "secondary";
    if (pointHalf(pt, ca) !== receivingHalf) {
      return { serveGrade: "+", receiveGrade: "/" }; // over-pass
    }
    const target = pixelForFormationPoint(
      PERFECT_SETTER_POSITION,
      ca,
      receivingIsPrimary,
    );
    const metresPerPixel = METRES_PER_HALF / halfScale(ca);
    const distMetres =
      Math.hypot(pt.x - target.x, pt.y - target.y) * metresPerPixel;
    if (distMetres <= 1) return { serveGrade: "-", receiveGrade: "#" };
    if (distMetres <= 2) return { serveGrade: "-", receiveGrade: "+" };
    if (distMetres <= 3) return { serveGrade: "!", receiveGrade: "!" };
    return { serveGrade: "+", receiveGrade: "-" };
  };


  // Build pixel-positioned slots for a team for the CURRENT render (used both
  // for drawing and for nearest-player lookups on gesture end). Positioning
  // matches the original CodingCourt.jsx model (origpos/recpos/recposopp/
  // basepos, selected via `formationFor`); the ROLE LABEL shown per slot
  // comes from the stable starting-lineup-based `roleByShirt` map.
  const slotsWithPixelsRef = useRef({ A: [], B: [] });
  const buildSlotsWithPixels = (ca) => {
    const out = { A: [], B: [] };
    ["A", "B"].forEach((key) => {
      const team = teamObj(key);
      const isPrimaryHalf = key === primaryTeamKey;
      const isServingNow = servingTeam === key;
      const ppos = formationFor(team, servingTeam, stage);
      const slots = buildCourtSlots(
        team,
        isServingNow,
        roleByShirtFor(key),
        activeLiberoIdx[key] ?? 0,
      );
      out[key] = slots.map((s) => ({
        ...s,
        pixel: pixelForSlot(s.seat, ppos, ca, isPrimaryHalf),
      }));
    });
    slotsWithPixelsRef.current = out;
    return out;
  };

  // `excludeRole` optionally skips slots with that role (e.g. "S") from
  // being picked as the nearest player — used so the setter can never be
  // attributed as the receiver of a serve just because their on-court
  // position happens to be closest to where the ball landed.
  const nearestSlot = (pt, slots, excludeRole) => {
    if (!pt || !slots || slots.length === 0) return null;
    let best = null;
    let bestD = Infinity;
    for (const s of slots) {
      if (s.shirtNumber == null) continue;
      if (excludeRole && s.role === excludeRole) continue;
      const d = Math.hypot(pt.x - s.pixel.x, pt.y - s.pixel.y);
      if (d < bestD) {
        bestD = d;
        best = s;
      }
    }
    return best;
  };

  // Same disc size used for players and for the bench-libero marker.
  const discRadius = (ca) => Math.max(15, halfScale(ca) * 0.0825);

  // Pixel position for the Nth (0-based) marker in a team's sideline row —
  // built out from `SECOND_LIBERO_POSITION`, each successive slot spaced
  // further along the baseline so multiple sideline markers never overlap.
  const sidelineSlotPixel = (key, ca, index) => {
    const spacing = 22; // formation-percentage units between slots
    const p = {
      ...SECOND_LIBERO_POSITION,
      y: SECOND_LIBERO_POSITION.y + spacing * index,
    };
    return pixelForFormationPoint(p, ca, key === primaryTeamKey);
  };

  // A team's serving player (position 1) is a middle blocker: that MB stays
  // on court instead of being substituted (see the `pos === 1 && isServingNow`
  // exception in `buildCourtSlots`), so the libero who'd otherwise be on
  // court for them sits out instead. Returns
  // `{ liberoShirt, liberoRole, mbShirt, mbRole }` — used to show that
  // sidelined libero on the sideline (see the drawing effect); the MB is NOT
  // drawn there since they're already shown in their normal on-court disc.
  // Returns null when not applicable (team not currently serving, no libero
  // designated, or the server isn't a middle blocker).
  const servingMBLiberoPair = (key) => {
    if (servingTeam !== key) return null;
    const team = teamObj(key);
    const liberoList = Array.isArray(team?.libero) ? team.libero : [];
    const activeIdx = activeLiberoIdx[key] ?? 0;
    const liberoShirt =
      liberoList.length > 0 ? (liberoList[activeIdx] ?? liberoList[0]) : null;
    if (liberoShirt == null) return null;
    const server = (team?.lineup ?? []).find((p) => positionOf(p) === 1);
    const mbShirt = shirtOf(server);
    if (mbShirt == null) return null;
    const mbRole = roleByShirtFor(key)?.[mbShirt] ?? "";
    if (mbRole !== "MB1" && mbRole !== "MB2") return null;
    const liberoRole =
      roleByShirtFor(key)?.[parseInt(String(liberoShirt), 10)] ?? "L";
    return { liberoShirt, liberoRole, mbShirt, mbRole };
  };

  // How many sideline slots the serving-MB's sidelined libero (if shown)
  // occupies for a team — only the libero is drawn on the sideline (the MB
  // stays on court); the "second libero" bench marker is drawn AFTER this
  // slot so the two never overlap.
  const servingMBPairSlotCount = (key) => (servingMBLiberoPair(key) ? 1 : 0);

  // The middle blocker(s) currently OFF the court because a libero is playing
  // in their back-row position (see the MB→libero swap in `buildCourtSlots`).
  // Derived from the already-computed slots so it honours the serving-MB
  // exception (a serving MB stays on court and is NOT listed here). Returns
  // `[{ shirt, role }]` so the benched MB's own disc can be shown on the
  // sideline instead of vanishing while the libero covers for them.
  const benchedMBs = (key) => {
    const slots = slotsWithPixelsRef.current?.[key] ?? [];
    const rbs = roleByShirtFor(key);
    const out = [];
    const seen = new Set();
    for (const s of slots) {
      if (s.isLibero && s.rawShirtNumber != null && !seen.has(s.rawShirtNumber)) {
        seen.add(s.rawShirtNumber);
        out.push({ shirt: s.rawShirtNumber, role: rbs?.[s.rawShirtNumber] ?? "MB" });
      }
    }
    return out;
  };
  const benchedMBSlotCount = (key) => benchedMBs(key).length;

  // Where a team's bench-libero marker is drawn — see `SECOND_LIBERO_POSITION`.
  const benchLiberoPixel = (key, ca) =>
    sidelineSlotPixel(
      key,
      ca,
      servingMBPairSlotCount(key) + benchedMBSlotCount(key),
    );

  // Returns { shirt, role } for a team's SECOND (currently off-court) libero,
  // or null if the team has fewer than 2 designated liberos. `role` comes
  // from the stable roleByShirt map (L1/L2, by original libero-list order —
  // NOT by on-court/bench status, which the user can toggle).
  const benchLibero = (key) => {
    const team = teamObj(key);
    const list = Array.isArray(team?.libero) ? team.libero : [];
    if (list.length < 2) return null;
    const activeIdx = activeLiberoIdx[key] ?? 0;
    const benchIdx = activeIdx === 0 ? 1 : 0;
    const shirt = list[benchIdx];
    if (shirt == null) return null;
    const role = roleByShirtFor(key)?.[parseInt(String(shirt), 10)] ?? "L";
    return { shirt, role };
  };

  // Swaps which of a team's 2 liberos is treated as "on court" vs "bench".
  const toggleBenchLibero = (key) => {
    setActiveLiberoIdx((prev) => ({
      ...prev,
      [key]: (prev[key] ?? 0) === 0 ? 1 : 0,
    }));
  };

  // Hit-tests a point against both teams' bench-libero markers (if shown);
  // returns the team key hit, or null. (The serving-MB/libero pairing
  // markers are purely informational — there's nothing to toggle there, so
  // they're not hit-tested.)
  const hitBenchLibero = (pt) => {
    const ca = computeCourtArea(
      containerRef.current?.clientWidth ?? 0,
      containerRef.current?.clientHeight ?? 0,
    );
    const r = discRadius(ca);
    for (const key of ["A", "B"]) {
      if (!benchLibero(key)) continue;
      const p = benchLiberoPixel(key, ca);
      if (Math.hypot(pt.x - p.x, pt.y - p.y) <= r * 1.1) return key;
    }
    return null;
  };

  // ---- drawing ----------------------------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || size.w === 0 || size.h === 0) return;
    canvas.width = size.w;
    canvas.height = size.h;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, size.w, size.h);
    const ca = computeCourtArea(size.w, size.h);
    const slots = buildSlotsWithPixels(ca);

    // Court fill
    ctx.fillStyle = "white"; //"#e8b062";
    ctx.fillRect(ca.left, ca.top, ca.width, ca.height);
    ctx.strokeStyle = "lightgray"; //"#ffffff";
    ctx.lineWidth = Math.max(1.5, halfScale(ca) * 0.01);
    ctx.strokeRect(ca.left, ca.top, ca.width, ca.height);

    // Net line
    ctx.beginPath();
    if (isVertical) {
      ctx.moveTo(ca.left - ca.width / 10, ca.top + ca.height / 2);
      ctx.lineTo(ca.left + ca.width + ca.width / 10, ca.top + ca.height / 2);
    } else {
      ctx.moveTo(ca.left + ca.width / 2, ca.top - ca.height / 10);
      ctx.lineTo(ca.left + ca.width / 2, ca.top + ca.height + ca.height / 10);
    }
    ctx.strokeStyle = "#333333";
    ctx.lineWidth = Math.max(2, halfScale(ca) * 0.015);
    ctx.stroke();

    // Attack (10ft) lines
    const attackDist = halfScale(ca) * 0.33;
    ctx.strokeStyle = "lightgray"; // "rgba(255,255,255,0.7)";
    ctx.lineWidth = Math.max(1, halfScale(ca) * 0.006);
    ctx.beginPath();
    if (isVertical) {
      ctx.moveTo(ca.left, ca.top + ca.height / 2 - attackDist);
      ctx.lineTo(ca.left + ca.width, ca.top + ca.height / 2 - attackDist);
      ctx.moveTo(ca.left, ca.top + ca.height / 2 + attackDist);
      ctx.lineTo(ca.left + ca.width, ca.top + ca.height / 2 + attackDist);
    } else {
      ctx.moveTo(ca.left + ca.width / 2 - attackDist, ca.top);
      ctx.lineTo(ca.left + ca.width / 2 - attackDist, ca.top + ca.height);
      ctx.moveTo(ca.left + ca.width / 2 + attackDist, ca.top);
      ctx.lineTo(ca.left + ca.width / 2 + attackDist, ca.top + ca.height);
    }
    ctx.stroke();

    // Perfect setting zone — a faint 1m-radius shadow around
    // PERFECT_SETTER_POSITION, drawn on whichever team's court is about to
    // receive the next touch (the serve receiver, or the digging team while
    // a rally is in general play), as a visual aid for pass/dig grading.
    const expectedReceiverKey = currentTeamKey === "A" ? "B" : "A";
    // const zoneCentre = pixelForFormationPoint(
    //   PERFECT_SETTER_POSITION,
    //   ca,
    //   expectedReceiverKey === primaryTeamKey,
    // );
    // const zoneRadius = halfScale(ca) / METRES_PER_HALF;
    // ctx.beginPath();
    // ctx.arc(zoneCentre.x, zoneCentre.y, zoneRadius, 0, 2 * Math.PI);
    // ctx.fillStyle = "rgba(17,24,39,0.10)";
    // ctx.fill();
    // ctx.strokeStyle = "rgba(17,24,39,0.25)";
    // ctx.lineWidth = 1;
    // ctx.stroke();

    // Players
    const radius = discRadius(ca); // 50% larger than the original 10 / 0.055
    ["A", "B"].forEach((key) => {
      const team = teamObj(key);
      const isServingNow = servingTeam === key;
      const isActingTeam =
        stage === "serve" ? servingTeam === key : currentTeamKey === key;
      const isPendingBlockTeam = pendingBlock?.teamKey === key;
      // In vertical orientation, the top team's labels sit above their discs
      // (rather than below, toward the net) so they don't crowd the middle.
      const labelAbove = isVertical && key === primaryTeamKey;
      const teamColour = team.colour || (key === "A" ? "lime" : "magenta");
      const textColour = contrastFor(teamColour);
      slots[key].forEach((s) => {
        if (s.shirtNumber == null) return;
        if (isActingTeam) {
          // Point toward the opposing court: "primary" half discs advance
          // toward the net line at the middle (down for vertical, right for
          // horizontal); "secondary" half discs advance the opposite way.
          // Drawn BEFORE the disc itself so the disc's fill/stroke/number
          // paint over the inner half of the arrow, leaving only the tip
          // poking out past the disc's edge — nothing inside the disc gets
          // obscured.
          const towardPrimary = key === primaryTeamKey;
          const dir = isVertical
            ? [0, towardPrimary ? 1 : -1]
            : [towardPrimary ? 1 : -1, 0];
          drawDiscArrow(ctx, s.pixel.x, s.pixel.y, radius, dir, teamColour);
        }
        ctx.beginPath();
        ctx.arc(s.pixel.x, s.pixel.y, radius, 0, 2 * Math.PI);
        ctx.fillStyle = s.isLibero ? textColour : teamColour;
        ctx.fill();
        // if (isActingTeam) {
          ctx.lineWidth = 1;
          ctx.strokeStyle = "#111827";
          ctx.stroke();
        // }
        if (isPendingBlockTeam && [2, 3, 4].includes(s.position)) {
          // Dashed amber ring — "tap this player to attribute the block".
          ctx.beginPath();
          ctx.arc(s.pixel.x, s.pixel.y, radius * 1.25, 0, 2 * Math.PI);
          ctx.setLineDash([4, 3]);
          ctx.lineWidth = 2;
          ctx.strokeStyle = "#f59e0b";
          ctx.stroke();
          ctx.setLineDash([]);
        }
        // if (s.isLibero) {
        //   // // Thin yellow ring just inside the disc's edge — a libero cue that
        //   // // doesn't hide the team colour underneath.
        //   // ctx.beginPath();
        //   // ctx.arc(s.pixel.x, s.pixel.y, radius * 0.8, 0, 2 * Math.PI);
        //   // ctx.lineWidth = Math.max(2, radius * 0.14);
        //   // ctx.strokeStyle = "#fef08a";
        //   // ctx.stroke();
        //   ctx.beginPath();
        //   ctx.arc(s.pixel.x, s.pixel.y, radius, 0, 2 * Math.PI);
        //   ctx.fillStyle = textColour;
        //   ctx.fill();
        // }
        ctx.fillStyle = s.isLibero ? teamColour : textColour;
        ctx.font = `300 ${Math.round(radius)}px Arial`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(String(s.shirtNumber), s.pixel.x, s.pixel.y + 0.09 * radius);
        drawRoleLabel(ctx, s, radius, labelAbove);
        void isServingNow;
      });
    });

    // Serving-MB's sidelined libero — when a middle blocker is serving
    // (position 1) they stay on court instead of being substituted (see
    // `buildCourtSlots`), so the libero who'd otherwise take their spot sits
    // out. Show only that libero on the sideline, styled exactly like an
    // on-court libero (reversed colours — see the `s.isLibero` branches in
    // the "Players" loop above), right before the "second libero" bench
    // marker (if any — see `servingMBPairSlotCount`, which shifts that
    // marker over to make room). The serving MB itself is NOT drawn here.
    ["A", "B"].forEach((key) => {
      const pair = servingMBLiberoPair(key);
      if (!pair) return;
      const team = teamObj(key);
      const teamColour = team.colour || (key === "A" ? "lime" : "magenta");
      const textColour = contrastFor(teamColour);
      const r2 = radius * 0.85;
      const p = sidelineSlotPixel(key, ca, 0);
      ctx.beginPath();
      ctx.arc(p.x, p.y, r2, 0, 2 * Math.PI);
      ctx.fillStyle = textColour;
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = "#111827";
      ctx.stroke();
      ctx.fillStyle = teamColour;
      ctx.font = `300 ${Math.round(r2)}px Arial`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(pair.liberoShirt), p.x, p.y);
    });

    // Benched middle blocker(s) — the MB currently replaced by a libero in the
    // back row (see `buildCourtSlots`) would otherwise disappear from the view
    // entirely. Show their own disc on the sideline (normal player colours +
    // role badge) so the full roster stays visible. Drawn after the serving-MB
    // sidelined libero (if any) and before the second-libero bench marker.
    ["A", "B"].forEach((key) => {
      const mbs = benchedMBs(key);
      if (mbs.length === 0) return;
      const team = teamObj(key);
      const teamColour = team.colour || (key === "A" ? "lime" : "magenta");
      const textColour = contrastFor(teamColour);
      const r2 = radius * 0.85;
      const base = servingMBPairSlotCount(key);
      mbs.forEach((mb, i) => {
        const p = sidelineSlotPixel(key, ca, base + i);
        ctx.beginPath();
        ctx.arc(p.x, p.y, r2, 0, 2 * Math.PI);
        ctx.fillStyle = teamColour;
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = "#111827";
        ctx.stroke();
        ctx.fillStyle = textColour;
        ctx.font = `300 ${Math.round(r2)}px Arial`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(String(mb.shirt), p.x, p.y);
        drawRoleLabel(ctx, { pixel: p, role: mb.role }, r2, false);
      });
    });

    // Bench libero marker — a team's SECOND designated libero (if any),
    // shown just outside their own baseline, styled like an on-court libero
    // (reversed colours). Tapping it swaps which libero is currently
    // treated as being on the court (see `benchLibero`/`toggleBenchLibero`).
    ["A", "B"].forEach((key) => {
      const info = benchLibero(key);
      if (!info) return;
      const team = teamObj(key);
      const teamColour = team.colour || (key === "A" ? "lime" : "magenta");
      const textColour = contrastFor(teamColour);
      const p = benchLiberoPixel(key, ca);
      const r2 = radius * 0.85;
      ctx.beginPath();
      ctx.arc(p.x, p.y, r2, 0, 2 * Math.PI);
      ctx.fillStyle = textColour;
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = "#111827";
      ctx.stroke();
      ctx.fillStyle = teamColour;
      ctx.font = `300 ${Math.round(r2)}px Arial`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(info.shirt), p.x, p.y);
    });

    // Drag preview
    if (isDrawing && startPoint && endPoint) {
      const colour = "#2563eb";
      ctx.beginPath();
      ctx.moveTo(startPoint.x, startPoint.y);
      if (midPoint) ctx.lineTo(midPoint.x, midPoint.y);
      if (midPoint2) ctx.lineTo(midPoint2.x, midPoint2.y);
      ctx.lineTo(endPoint.x, endPoint.y);
      ctx.strokeStyle = colour;
      ctx.lineWidth = 3;
      ctx.stroke();
      drawArrowHead(
        ctx,
        midPoint2 ?? midPoint ?? startPoint,
        endPoint,
        14,
        0.3,
        colour,
        colour,
      );
      [midPoint, midPoint2].filter(Boolean).forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius * (pulse ? 0.9 : 0.6), 0, 2 * Math.PI);
        ctx.fillStyle = "rgba(37,99,235,0.35)";
        ctx.fill();
        ctx.strokeStyle = colour;
        ctx.lineWidth = 2;
        ctx.stroke();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    size,
    isDrawing,
    startPoint,
    midPoint,
    midPoint2,
    endPoint,
    pulse,
    pendingBlock,
    activeLiberoIdx,
    orientation,
    servingTeam,
    teamA,
    teamB,
    primaryTeamKey,
    stage,
    currentTeamKey,
    events,
  ]);

  // Pulse animation for the mid-point marker while a pause is active.
  useEffect(() => {
    if (!midPoint) return;
    const id = setInterval(() => setPulse((p) => !p), 350);
    return () => clearInterval(id);
  }, [midPoint]);

  // ---- gesture handling ---------------------------------------------------
  const relativePoint = (clientX, clientY) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const beginDrag = (pt) => {
    if (stage === "serve") {
      // A serve must be dragged from the serving zone behind the court, on
      // the server's own side — ignore mousedown/touchstart anywhere else.
      const ca = computeCourtArea(
        containerRef.current?.clientWidth ?? 0,
        containerRef.current?.clientHeight ?? 0,
      );
      if (!isInServeZone(pt, ca, currentTeamKey)) return;
    }
    setIsDrawing(true);
    setStartPoint(pt);
    setEndPoint(pt);
    setMidPoint(null);
    setMidPoint2(null);
    setPulse(false);
  };

  const moveDrag = (pt) => {
    if (!isDrawing) return;
    setEndPoint(pt);
    const time = new Date();
    time.setMilliseconds(time.getMilliseconds() + 200);
    restart(time, true);
  };

  const endDrag = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    processGesture(
      startPoint,
      midPoint,
      midPoint2,
      endPointRef.current ?? endPoint,
    );
    setStartPoint(null);
    setMidPoint(null);
    setMidPoint2(null);
    setEndPoint(null);
  };

  // Resolves a pending "tap to select blocker" state (see `processGesture`
  // case 1): the next tap/click selects the nearest player, on the
  // defending team, to attribute the deferred Block event to — but ONLY if
  // the tap actually lands on one of the eligible (front-row, dashed-ring)
  // blockers; a click anywhere else cancels the pending block without
  // recording any event.
  const resolvePendingBlock = (pt) => {
    const ca = computeCourtArea(
      containerRef.current?.clientWidth ?? 0,
      containerRef.current?.clientHeight ?? 0,
    );
    const hitRadius = discRadius(ca) * 1.3;
    const slots = slotsWithPixelsRef.current;
    const candidates = (slots[pendingBlock.teamKey] ?? []).filter(
      (s) => s.shirtNumber != null && [2, 3, 4].includes(s.position),
    );
    let blocker = null;
    let bestD = Infinity;
    for (const s of candidates) {
      const d = Math.hypot(pt.x - s.pixel.x, pt.y - s.pixel.y);
      if (d <= hitRadius && d < bestD) {
        bestD = d;
        blocker = s;
      }
    }
    if (!blocker) {
      // Clicked away from any eligible blocker — don't register a block.
      setPendingBlock(null);
      return;
    }
    const blockEv = makeEvent(
      pendingBlock.teamKey,
      blocker.shirtNumber,
      "B",
      pendingBlock.evaluation,
      pendingBlock.zoneFrom,
      null,
      null,
      null,
      pendingBlock.ballStartPct,
    );
    if (pendingBlock.compoundPaired) blockEv.compoundPaired = true;
    onEvents?.([blockEv]);
    setPendingBlock(null);
  };

  const onMouseDown = (e) => {
    const pt = relativePoint(e.clientX, e.clientY);
    if (pendingBlock) {
      resolvePendingBlock(pt);
      return;
    }
    const benchKey = hitBenchLibero(pt);
    if (benchKey) {
      toggleBenchLibero(benchKey);
      return;
    }
    beginDrag(pt);
  };
  const onMouseMove = (e) => moveDrag(relativePoint(e.clientX, e.clientY));
  const onMouseUp = () => endDrag();
  const onTouchStart = (e) => {
    const touch = e.touches[0];
    const pt = relativePoint(touch.clientX, touch.clientY);
    if (pendingBlock) {
      resolvePendingBlock(pt);
      return;
    }
    const benchKey = hitBenchLibero(pt);
    if (benchKey) {
      toggleBenchLibero(benchKey);
      return;
    }
    beginDrag(pt);
  };
  const onTouchMove = (e) => {
    const touch = e.touches[0];
    moveDrag(relativePoint(touch.clientX, touch.clientY));
  };
  const onTouchEnd = () => endDrag();

  // ---- rules engine -------------------------------------------------------
  const processGesture = (start, mid, mid2, end) => {
    if (!start || !end) return;
    const size2 = {
      w: containerRef.current?.clientWidth ?? 0,
      h: containerRef.current?.clientHeight ?? 0,
    };
    const ca = computeCourtArea(size2.w, size2.h);
    const slots = slotsWithPixelsRef.current;
    const newEvents = [];

    const zStart = zoneForPoint(start, ca);
    const zMidFull = mid ? zoneAndSubzoneForPoint(mid, ca) : null;
    const zMid = zMidFull?.zone ?? null;
    const inBoundsEnd = isInBounds(end, ca);
    const zEndFull = inBoundsEnd ? zoneAndSubzoneForPoint(end, ca) : null;
    const zEnd = zEndFull?.zone ?? null;

    // Precise dragged-point percent coords for each touch, persisted onto
    // the events built below (see `canonicalPctForPoint`/`makeEvent`) so
    // ball paths drawn here render with the same pixel-accurate precision
    // DVMatchCoding stores for a manually-dragged edit, instead of always
    // snapping to a zone+subzone quadrant's centre.
    const pStart = canonicalPctForPoint(start, ca);
    const pMid = mid ? canonicalPctForPoint(mid, ca) : null;
    const pEnd = canonicalPctForPoint(end, ca);
    const pMid2 = mid2 ? canonicalPctForPoint(mid2, ca) : null;

    if (stage === "serve") {
      const serverTeamKey = currentTeamKey;
      const receivingTeamKey = serverTeamKey === "A" ? "B" : "A";
      const serverSlot = slots[serverTeamKey]?.find((s) => s.position === 1);

      // The gesture never actually left the server's own serving zone (e.g.
      // an accidental click/tap right where the drag started) — ignore the
      // whole sequence rather than recording a bogus serve.
      if (isInServeZone(end, ca, serverTeamKey)) return;

      if (!mid) {
        // No pause/touch before the ball terminated. If it landed untouched
        // INSIDE the opposition's court (in bounds, opposite half from the
        // server), the serve is an ace ("#") and that's also recorded as a
        // reception error ("=") against their nearest player. If it went
        // anywhere else — out of bounds, or back on the server's own side —
        // the serve itself is an error ("="), and no reception event is
        // created (the receiving team never had a chance to play it).
        const landedOtherSide =
          inBoundsEnd && pointHalf(end, ca) !== pointHalf(start, ca);
        newEvents.push(
          makeEvent(
            serverTeamKey,
            serverSlot?.shirtNumber,
            "S",
            landedOtherSide ? "#" : "=",
            zStart,
            zEnd,
            null,
            zEndFull?.subzone ?? null,
            pStart,
            pEnd,
          ),
        );
        if (landedOtherSide) {
          const receiver = nearestSlot(end, slots[receivingTeamKey], "S");
          const recEv = makeEvent(
            receivingTeamKey,
            receiver?.shirtNumber,
            "R",
            "=",
            zEnd,
            null,
            zEndFull?.subzone ?? null,
            pEnd,
          );
          // The serve ace already scored this rally's point — mark the paired
          // reception error so applyEvents doesn't award a SECOND point.
          recEv.compoundPaired = true;
          newEvents.push(recEv);
        }
      } else {
        const receivingIsPrimary = receivingTeamKey === primaryTeamKey;
        const { serveGrade, receiveGrade: passGrade } =
          serveReceiveGradesForPoint(end, ca, receivingIsPrimary);
        newEvents.push(
          makeEvent(
            serverTeamKey,
            serverSlot?.shirtNumber,
            "S",
            serveGrade,
            zStart,
            zMid,
            null,
            zMidFull?.subzone ?? null,
            pStart,
            pMid,
          ),
        );
        const receiver = nearestSlot(mid, slots[receivingTeamKey], "S");
        newEvents.push(
          makeEvent(
            receivingTeamKey,
            receiver?.shirtNumber,
            "R",
            passGrade,
            zMid,
            zEnd,
            null,
            zEndFull?.subzone ?? null,
            pMid,
            pEnd,
          ),
        );
        // An over-pass sends the ball straight back over the net into the
        // SERVING team's court — there's no setter call for the receiving
        // team (they never controlled it), and the serving team becomes the
        // attacking team (they now have to play the free ball straight back).
        if (passGrade !== "/") {
          const setterShirt = teamObj(receivingTeamKey).setter;
          newEvents.push(
            makeEvent(receivingTeamKey, setterShirt, "E", "+", zEnd, null, null, null, pEnd),
          );
        }
      }
    } else {
      const attackerTeamKey = currentTeamKey;
      const defenderTeamKey = attackerTeamKey === "A" ? "B" : "A";
      const attacker = nearestSlot(start, slots[attackerTeamKey]);

      if (!mid) {
        const landedOtherSide =
          inBoundsEnd && pointHalf(end, ca) !== pointHalf(start, ca);
        newEvents.push(
          makeEvent(
            attackerTeamKey,
            attacker?.shirtNumber,
            "A",
            landedOtherSide ? "#" : "=",
            zStart,
            zEnd,
            null,
            zEndFull?.subzone ?? null,
            pStart,
            pEnd,
          ),
        );
      } else if (isNearNet(mid, ca)) {
        // An attack touched near the net — pause (1) at the touch point.
        if (!mid2) {
          // Case 1 — released right after the touch: whether the ball ends
          // up back in the attacking team's own court or carries on into
          // the defending team's court, the Attack event is recorded
          // immediately; the Block event is DEFERRED until the user taps
          // the blocking player (see `pendingBlock` / `resolvePendingBlock`).
          const sameSideAsAttacker =
            inBoundsEnd && pointHalf(end, ca) === pointHalf(start, ca);
          if (sameSideAsAttacker) {
            // 1a — deflected back into the attacker's own court: blocked point.
            newEvents.push(
              makeEvent(
                attackerTeamKey,
                attacker?.shirtNumber,
                "A",
                "/",
                zStart,
                zMid,
                null,
                zMidFull?.subzone ?? null,
                pStart,
                pMid,
              ),
            );
            setPendingBlock({
              teamKey: defenderTeamKey,
              evaluation: "#",
              zoneFrom: zMid,
              ballStartPct: pMid,
            });
          } else {
            // 1b — touched-out or carried on past the block: kill.
            newEvents.push(
              makeEvent(
                attackerTeamKey,
                attacker?.shirtNumber,
                "A",
                "#",
                zStart,
                zMid,
                null,
                zMidFull?.subzone ?? null,
                pStart,
                pMid,
              ),
            );
            setPendingBlock({
              teamKey: defenderTeamKey,
              evaluation: "=",
              zoneFrom: zMid,
              ballStartPct: pMid,
              // The attack kill above already scored this rally — the paired
              // block error must NOT award a second point.
              compoundPaired: true,
            });
          }
        } else {
          // Case 2 — the user paused again at (2)/mid2 (where the touched
          // ball was dug) before moving on to a final release point (3)/end.
          // This is the "covered block" 3-legged path (attacker -> blocker ->
          // defender -> setter): the rally is NOT terminated, so it records
          // three touches — Attack (start -> mid, into the block), Block
          // (mid -> mid2, the block deflection that kept the ball alive) and
          // Defence (mid2 -> end, the dig up to the setter). Whichever team's
          // half mid2 falls in becomes the team in control: mid2 on the
          // attacker's side means the attacking team covered its own blocked
          // ball (rule 1.c); mid2 on the blocking team's side means the
          // blocking team covered it (rule 2.b). An over-pass to `end`, if
          // any, is handled the same way as elsewhere via `passGradeForPoint`
          // and the existing stage/currentTeamKey derivation.
          const zMid2Full = zoneAndSubzoneForPoint(mid2, ca);
          const zMid2 = zMid2Full?.zone ?? null;
          const diggingTeamKey =
            pointHalf(mid2, ca) === "primary"
              ? primaryTeamKey
              : secondaryTeamKey;
          newEvents.push(
            makeEvent(
              attackerTeamKey,
              attacker?.shirtNumber,
              "A",
              diggingTeamKey === attackerTeamKey ? "+" : "-",
              zStart,
              zMid,
              null,
              zMidFull?.subzone ?? null,
              pStart,
              pMid,
            ),
          );
          // The block touch that kept the ball in play. Attributed to the
          // nearest FRONT-ROW (positions 2/3/4) player of the blocking team
          // to the net-touch point; graded "+" (a controlled/soft block —
          // ball stays up), which never scores (see `rallyWinnerFor`), so
          // play continues. Not deferred like the terminal-block cases: the
          // gesture is already complete and its events are emitted together
          // in order, so the blocker is auto-picked instead of waiting for a
          // tap (which would arrive out of sequence, after the dig).
          const frontRowBlockers = (slots[defenderTeamKey] ?? []).filter(
            (s) => [2, 3, 4].includes(s.position),
          );
          const blocker = nearestSlot(mid, frontRowBlockers);
          if (blocker) {
            newEvents.push(
              makeEvent(
                defenderTeamKey,
                blocker.shirtNumber,
                "B",
                "+",
                zMid,
                zMid2,
                zMidFull?.subzone ?? null,
                zMid2Full?.subzone ?? null,
                pMid,
                pMid2,
              ),
            );
          }
          const digger = nearestSlot(mid2, slots[diggingTeamKey]);
          const digGrade = passGradeForPoint(
            end,
            ca,
            diggingTeamKey === primaryTeamKey,
          );
          newEvents.push(
            makeEvent(
              diggingTeamKey,
              digger?.shirtNumber,
              "D",
              digGrade,
              zMid2,
              zEnd,
              null,
              zEndFull?.subzone ?? null,
              pMid2,
              pEnd,
            ),
          );
        }
      } else {
        // 5.ii — a plain dig (mid not near the net): defence, then a set.
        newEvents.push(
          makeEvent(
            attackerTeamKey,
            attacker?.shirtNumber,
            "A",
            "+",
            zStart,
            zMid,
            null,
            zMidFull?.subzone ?? null,
            pStart,
            pMid,
          ),
        );
        const digger = nearestSlot(mid, slots[defenderTeamKey]);
        const digGrade = passGradeForPoint(
          end,
          ca,
          defenderTeamKey === primaryTeamKey,
        );
        newEvents.push(
          makeEvent(
            defenderTeamKey,
            digger?.shirtNumber,
            "D",
            digGrade,
            zMid,
            zEnd,
            null,
            zEndFull?.subzone ?? null,
            pMid,
            pEnd,
          ),
        );
        // Same over-pass handoff as reception: a dig that sails back over
        // the net has no setter call, and the OTHER team becomes the
        // attacking team for the next gesture.
        if (digGrade !== "/") {
          const setterShirt = teamObj(defenderTeamKey).setter;
          newEvents.push(
            makeEvent(defenderTeamKey, setterShirt, "E", "+", zEnd, null, null, null, pEnd),
          );
        }
      }
    }

    if (newEvents.length > 0) onEvents?.(newEvents);
  };

  // "Error" buttons (one at each team's baseline end): record a team error —
  // an event with NO player ("00") and result "=" (which DVMatchCoding's
  // `rallyWinnerFor` scores as a point to the OTHER team) — then let it flow
  // through the normal engine. The user can afterwards edit the event in the
  // Event Editor to supplement the player and/or the actual event type; the
  // skill defaults to Attack ("A") only as an editable placeholder.
  const handleTeamError = (teamKey) => {
    const ev = makeEvent(teamKey, null, "A", "=", null, null, null, null);
    onEvents?.([ev]);
  };

  // Renders a team's Error button, absolutely positioned at that team's own
  // end of the court (top/bottom in vertical, left/right in horizontal),
  // coloured with the team's colour.
  const renderErrorButton = (key) => {
    const team = teamObj(key);
    const teamColour = team.colour || (key === "A" ? "lime" : "magenta");
    const textColour = contrastFor(teamColour);
    const isPrimary = key === primaryTeamKey;
    // Primary team (top/left side) button → top-left corner; secondary team
    // (bottom/right side) button → bottom-right corner.
    const posStyle = isPrimary
      ? { top: 4, left: 4 }
      : { bottom: 4, right: 4 };
    return (
      <button
        key={`err-${key}`}
        type="button"
        className="btn btn-xs absolute z-10 shadow font-bold"
        style={{
          ...posStyle,
          backgroundColor: teamColour,
          color: textColour,
          borderColor: textColour,
        }}
        onClick={() => handleTeamError(key)}
        title={t("Record a team error (no player) — edit later to add the player and event type")}
      >
        {t("Error")}
      </button>
    );
  };

  const hintText = pendingBlock
    ? t("Tap the blocking player.")
    : t(
        "Drag to draw the ball path; pause briefly to mark a touch (reception/dig/block), release to finish.",
      );

  return (
    <div
      className={`flex flex-col items-center bg-base-200 rounded h-full min-h-0 ${
        isVertical ? "gap-0 p-0" : "gap-1 p-2"
      }`}
    >
      {!isVertical && (
        <div className="text-xs text-base-content/60 self-stretch flex justify-between items-center shrink-0">
          <span>{t("Draw & Code")}</span>
          <button
            className="btn btn-xs btn-accent"
            onClick={onGoLive}
            title={t("Return to live coding")}
          >
            {t("LIVE")}
          </button>
        </div>
      )}
      <div ref={containerRef} className="flex-1 min-h-0 w-full relative">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full"
          style={{ touchAction: "none", cursor: "crosshair" }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        />
        {renderErrorButton(primaryTeamKey)}
        {renderErrorButton(secondaryTeamKey)}
        {/* Vertical orientation: the header (LIVE) and footer (help hint) are
            removed to give the Error buttons the full window height; LIVE is
            an overlay in the top-right corner and the help hint is behind a
            small "i" button in the bottom-left corner. */}
        {isVertical && (
          <>
            <button
              className="btn btn-xs btn-accent absolute z-10 shadow"
              style={{ top: 4, right: 4 }}
              onClick={onGoLive}
              title={t("Return to live coding")}
            >
              {t("LIVE")}
            </button>
            <button
              className="btn btn-xs btn-circle btn-ghost absolute z-10 shadow bg-base-100"
              style={{ bottom: 4, left: 4 }}
              onClick={() => setShowInfo(true)}
              title={t("Show help")}
            >
              ℹ️
            </button>
            {showInfo && (
              <div
                className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 p-4"
                onClick={() => setShowInfo(false)}
              >
                <div
                  className="bg-base-100 rounded shadow-lg p-3 max-w-xs text-xs text-base-content/80"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div>{hintText}</div>
                  <div className="mt-2 text-right">
                    <button
                      className="btn btn-xs"
                      onClick={() => setShowInfo(false)}
                    >
                      {t("Close")}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {!isVertical && (
        <div className="text-[10px] text-base-content/50 text-center shrink-0 h-8 flex items-center justify-center overflow-hidden px-2">
          {hintText}
        </div>
      )}
    </div>
  );
}
